"use client";

import { c as _c } from "react/compiler-runtime";
import { ChevronRightIcon, XIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { useCart } from "@/contexts/CartContext";
import { useStore } from "@/contexts/StoreContext";
import { useCountrySwitch } from "@/hooks/useCountrySwitch";
import { readCookie, readGeo } from "@/lib/consent/state";
import { currentDomain, type DomainMarket } from "@/lib/domain/host-map";
import { suggestStore } from "@/lib/domain/suggest-store";
const DISMISS_COOKIE = "tkf_store_prompt";
const DISMISS_MAX_AGE_SECONDS = 30 * 24 * 60 * 60;
function rememberDismissal(): void {
  try {
    const secure = window.location.protocol === "https:" ? "; Secure" : "";
    // biome-ignore lint/suspicious/noDocumentCookie: per-domain "Stay here" memory, read on the client
    document.cookie = `${DISMISS_COOKIE}=dismissed; Path=/; Max-Age=${DISMISS_MAX_AGE_SECONDS}; SameSite=Lax${secure}`;
  } catch {
    // Cookies blocked: the bar just reappears on the next page.
  }
}

/**
 * Suggests the storefront domain that serves the visitor's country (from the
 * middleware's `tkf_geo` cookie) — a prompt, never a redirect, so crawlers and
 * travellers keep the page they asked for. Client-only (cookies), so it adds
 * nothing to cached HTML. Mounted in the storefront layout only: switching
 * domain mid-checkout would drop the cart.
 *
 * Compact by design: on mobile it's one tappable line plus a 44px dismiss; the
 * full sentence and the explicit "Go" button appear from `sm` up.
 */
export function StorePromptBar() {
  const $ = _c(58);
  const t = useTranslations("storePrompt");
  const {
    markets
  } = useStore();
  const {
    cart
  } = useCart();
  const {
    regionHref
  } = useCountrySwitch();
  const [target, setTarget] = useState(null);
  let t0;
  let t1;
  if ($[0] !== markets) {
    t0 = () => {
      if (readCookie(DISMISS_COOKIE)) {
        return;
      }
      setTarget(suggestStore(readGeo(), markets, currentDomain()));
    };
    t1 = [markets];
    $[0] = markets;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  if (!target) {
    return null;
  }
  let t2;
  if ($[3] !== regionHref || $[4] !== target) {
    t2 = regionHref(target);
    $[3] = regionHref;
    $[4] = target;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const href = t2;
  if (!href) {
    return null;
  }
  const store = target.shortLabel;
  const hasCartItems = (cart?.total_quantity ?? 0) > 0;
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      rememberDismissal();
      setTarget(null);
    };
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const dismiss = t3;
  let t4;
  if ($[7] !== t) {
    t4 = t("label");
    $[7] = t;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const t5 = target.flag;
  let t6;
  if ($[9] !== store || $[10] !== t || $[11] !== target.currency) {
    t6 = t("short", {
      store,
      currency: target.currency
    });
    $[9] = store;
    $[10] = t;
    $[11] = target.currency;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t6 || $[14] !== target.flag) {
    t7 = <span>{t5} {t6}</span>;
    $[13] = t6;
    $[14] = target.flag;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <ChevronRightIcon aria-hidden="true" className="size-4 shrink-0" />;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== href || $[18] !== t7) {
    t9 = <a href={href} className="flex min-h-11 min-w-0 flex-1 items-center gap-1 text-left font-medium sm:hidden">{t7}{t8}</a>;
    $[17] = href;
    $[18] = t7;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== target.flag) {
    t10 = <span aria-hidden="true">{target.flag}</span>;
    $[20] = target.flag;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== store || $[23] !== t || $[24] !== target.currency) {
    t11 = t("message", {
      store,
      currency: target.currency
    });
    $[22] = store;
    $[23] = t;
    $[24] = target.currency;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== hasCartItems || $[27] !== t) {
    t12 = hasCartItems && <> {t("cartNote")}</>;
    $[26] = hasCartItems;
    $[27] = t;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== t10 || $[30] !== t11 || $[31] !== t12) {
    t13 = <p className="hidden min-w-0 flex-1 sm:block">{t10}{" "}{t11}{t12}</p>;
    $[29] = t10;
    $[30] = t11;
    $[31] = t12;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== store || $[34] !== t) {
    t14 = t("go", {
      store
    });
    $[33] = store;
    $[34] = t;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== href || $[37] !== t14) {
    t15 = <Button asChild={true} size="sm" variant="secondary" className="hidden sm:inline-flex"><a href={href}>{t14}</a></Button>;
    $[36] = href;
    $[37] = t14;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  let t16;
  if ($[39] !== t) {
    t16 = t("stay");
    $[39] = t;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  let t17;
  if ($[41] !== t16) {
    t17 = <span className="sr-only sm:not-sr-only">{t16}</span>;
    $[41] = t16;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  let t18;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <XIcon aria-hidden="true" />;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  let t19;
  if ($[44] !== t17) {
    t19 = <Button variant="ghost" onClick={dismiss} className="-mr-2 h-11 min-w-11 px-2 text-neutral-100 hover:bg-white/10 hover:text-white sm:mr-0 sm:h-9">{t17}{t18}</Button>;
    $[44] = t17;
    $[45] = t19;
  } else {
    t19 = $[45];
  }
  let t20;
  if ($[46] !== hasCartItems || $[47] !== t) {
    t20 = hasCartItems && <p className="w-full pb-2 text-xs text-neutral-300 sm:hidden">{t("cartNote")}</p>;
    $[46] = hasCartItems;
    $[47] = t;
    $[48] = t20;
  } else {
    t20 = $[48];
  }
  let t21;
  if ($[49] !== t13 || $[50] !== t15 || $[51] !== t19 || $[52] !== t20 || $[53] !== t9) {
    t21 = <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-x-4 px-4 sm:py-1.5">{t9}{t13}{t15}{t19}{t20}</div>;
    $[49] = t13;
    $[50] = t15;
    $[51] = t19;
    $[52] = t20;
    $[53] = t9;
    $[54] = t21;
  } else {
    t21 = $[54];
  }
  let t22;
  if ($[55] !== t21 || $[56] !== t4) {
    t22 = <section aria-label={t4} className="bg-neutral-900 text-sm text-neutral-100">{t21}</section>;
    $[55] = t21;
    $[56] = t4;
    $[57] = t22;
  } else {
    t22 = $[57];
  }
  return t22;
}